/**
 * SPEC v0.9.5 §4 — what the result-card loaders already fetched, collected for the
 * "สรุป" sheet so it never calls an API again. Each loader publishes its dimension for
 * the current point (a render generation guards against a previous point's late reply).
 * Values here are the raw, displayable pieces; summary-ui maps them to decideSummary().
 */
import type { Dim } from "@flowsight/sim";
import type { RainData } from "./rain-forecast";
import type { DamRainData } from "./dam-rain";
import type { LowerRiverData } from "./lower-river";
import type { ReleaseChange } from "@flowsight/sim";

export interface PlaceInfo { t: string; a: string; p: string; bkk: boolean; declared: boolean; declaredDate: string | null; label: string }
export interface GistdaInfo { level: string | null; nearestKm: number | null; observedAt: string | null; source: string | null; floodedInRadius: boolean }
export interface RoadGaugeInfo { maxDepthCm: number | null; name: string | null; measuredAt: string | null; distKm: number | null }
export interface StationInfo {
  count: number;
  nearest: { sid?: number | null; name: string; river: string | null; status: string; diffBankM: number | null; distKm: number; datetime: string | null } | null;
  maxOverBankM: number | null; overName: string | null; nearBank: boolean; nearName: string | null;
  /** D2: nearest station on the same river outside the radius (≤30 km) when none is inside */
  outside: { sid?: number | null; name: string; river: string | null; status: string; diffBankM: number | null; distKm: number; datetime: string | null } | null;
}
/** D1: the nearest main river the summary follows (≤30 km, independent of the radius) */
export interface RiverInfo { name: string | null; distKm: number; reachId: number; nearest?: { name: string; distKm: number } | null } // main river (D1) + the closer stream, if any
export interface AdviceInfo { show: boolean; reason: string }
export interface DamInfo {
  name: string; percent: number | null; outflowCms: number | null; valueDate: string | null;
  change: ReleaseChange | null; releaseAmber: boolean; releaseText: string | null;
  c13: { outflowCms: number; change: ReleaseChange | null } | null;
}
/** v0.9.10 B — ground height (Open-Meteo · Copernicus GLO-90). Information only: never a water depth,
 *  never compared with a gauge level, never an input of decideSummary. */
export interface ElevationInfo { elevationM: number; compare: "higher" | "lower" | "similar" | null; diffM: number | null; source: string }
/** v0.9.10 A — the summary river row's station: measured 6-h trend */
export interface StationTrendInfo { name: string; kind: "up" | "down" | "flat" | "insufficient"; cmPerHour: number | null }
export interface StormInfo { count: number; name: string | null; nearestKm: number | null; affects: boolean; updatedAt: string | null }

export interface SummaryState {
  gen: number;
  from: { lat: number; lon: number } | null;
  radiusKm: number;
  centerLabel: string | null;
  place: Dim<PlaceInfo>;
  gistda: Dim<GistdaInfo>;
  roadGauge: Dim<RoadGaugeInfo>;
  station: Dim<StationInfo>;
  rain: Dim<RainData>;
  advice: Dim<AdviceInfo>;
  damRain: Dim<DamRainData>;
  upstreamDam: Dim<DamInfo>;
  lowerRiver: Dim<LowerRiverData>;
  storm: Dim<StormInfo>;
  river: Dim<RiverInfo>;
  elevation: Dim<ElevationInfo>;
  stationTrend: Dim<StationTrendInfo>;
}

const LOADING = { state: "loading" } as const;
let st: SummaryState = fresh(0, null, 3, null, LOADING);
const subs = new Set<() => void>();

function fresh(gen: number, from: SummaryState["from"], radiusKm: number, centerLabel: string | null, storm: Dim<StormInfo>): SummaryState {
  return { gen, from, radiusKm, centerLabel, place: LOADING, gistda: LOADING, roadGauge: LOADING, station: LOADING, rain: LOADING, advice: LOADING, damRain: LOADING, upstreamDam: LOADING, lowerRiver: LOADING, storm, river: LOADING, elevation: LOADING, stationTrend: LOADING };
}

/** A new point was analysed → every dimension back to "loading" (storms are global, kept). */
export function resetSummary(gen: number, from: { lat: number; lon: number }, radiusKm: number, centerLabel: string | null): void {
  st = fresh(gen, from, radiusKm, centerLabel, st.storm);
  emit();
}
export function clearSummary(): void { st = fresh(st.gen + 1, null, st.radiusKm, null, st.storm); emit(); }

type Key = Exclude<keyof SummaryState, "gen" | "from" | "radiusKm" | "centerLabel">;
/** Loaders call this; a reply for an older point (gen ≠ current) is ignored. */
export function publish<K extends Key>(gen: number, key: K, value: SummaryState[K]): void {
  if (key !== "storm" && gen !== st.gen) return;
  st = { ...st, [key]: value };
  emit();
}
export const okDim = <T>(value: T): Dim<T> => ({ state: "ok", value });
export const errDim = { state: "error" } as const;
export const naDim = { state: "na" } as const;

export function summaryState(): SummaryState { return st; }
export function hasPoint(): boolean { return st.from != null; }
export function onSummaryChange(fn: () => void): () => void { subs.add(fn); return () => subs.delete(fn); }
function emit(): void { for (const fn of subs) fn(); }
